import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ClickAwayListener, Grow, Paper, Popper } from '@mui/material';
import { FC, useMemo, useRef, useState } from 'react';
import { WorkflowDto } from 'src/generated/services/TFinancialApi';

interface WorkflowDropdownProps {
  workflows: WorkflowDto[];
  selectedWorkflowId: string;
  onWorkflowSelect: (workflowId: string) => void;
  onAddNew: () => void;
  onEdit: (workflow: WorkflowDto) => void;
}

export const WorkflowDropdown: FC<WorkflowDropdownProps> = ({ workflows, selectedWorkflowId, onWorkflowSelect, onAddNew, onEdit }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleWorkflowSelect = (workflowId: string) => {
    onWorkflowSelect(workflowId);
    setIsDropdownOpen(false);
  };

  const handleToggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleDropdown();
    } else if (event.key === 'Escape') {
      setIsDropdownOpen(false);
    }
  };

  const handleCloseDropdown = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setIsDropdownOpen(false);
  };

  const handleAddNew = () => {
    onAddNew();
    setIsDropdownOpen(false);
  };

  const handleEdit = (workflow: WorkflowDto) => {
    onEdit(workflow);
    setIsDropdownOpen(false);
  };

  const selectedWorkflowLabel = useMemo(() => {
    if (!selectedWorkflowId || !workflows) return 'Select Workflow';
    const workflow = workflows.find((w) => w.id === selectedWorkflowId);
    return workflow?.name || workflow?.description || 'Select Workflow';
  }, [selectedWorkflowId, workflows]);

  return (
    <div className='relative'>
      <div
        ref={anchorRef}
        onClick={handleToggleDropdown}
        onKeyDown={handleKeyDown}
        role='button'
        tabIndex={0}
        aria-haspopup='true'
        aria-expanded={isDropdownOpen}
        className='flex items-center justify-between min-w-[240px] px-16 py-10 bg-white border border-neutral-30 rounded-8 cursor-pointer hover:border-neutral-40 transition-colors'
      >
        <span className='text-14 xl:text-16 text-neutral-80'>{selectedWorkflowLabel}</span>
        <KeyboardArrowDownIcon className={`text-neutral-60 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fontSize='small' />
      </div>
      <Popper
        open={isDropdownOpen}
        anchorEl={anchorRef.current}
        role={undefined}
        placement='bottom-start'
        transition
        disablePortal
        style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper className='mt-4 shadow-lg border border-neutral-20 rounded-8 overflow-hidden'>
              <ClickAwayListener onClickAway={handleCloseDropdown}>
                <div className='py-8 flex flex-col'>
                  <button
                    onClick={handleAddNew}
                    className='w-full flex items-center gap-8 px-16 py-12 text-primary hover:bg-neutral-10 transition-colors border-b border-neutral-20'
                  >
                    <AddIcon className='text-primary-main' fontSize='small' />
                    <span className='text-14 font-medium text-primary-main'>Add New</span>
                  </button>

                  <div className='flex-1 flex flex-col max-h-[275px] overflow-y-auto'>
                    {workflows?.map((workflow) => {
                      const isSelected = workflow.id === selectedWorkflowId;
                      return (
                        <div
                          key={workflow.id}
                          onClick={() => handleWorkflowSelect(workflow.id || '')}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleWorkflowSelect(workflow.id || '');
                            }
                          }}
                          role='option'
                          tabIndex={0}
                          aria-selected={isSelected}
                          className='flex items-center justify-between px-16 py-12 hover:bg-neutral-10 cursor-pointer transition-colors group'
                        >
                          <div className='flex-1'>
                            <div className='text-14 font-medium text-neutral-80 max-w-[200px] truncate'>
                              {workflow.name || workflow.description || `Workflow ${workflow.id}`}
                            </div>
                            {workflow.description && <div className='text-12 text-neutral-60 mt-2'>{workflow.description}</div>}
                          </div>
                          <div className='flex items-center gap-8'>
                            {isSelected && <CheckIcon className='text-primary-main' fontSize='small' />}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(workflow);
                              }}
                              aria-label={`Edit ${workflow.name || workflow.description || `Workflow ${workflow.id}`}`}
                              className='opacity-0 group-hover:opacity-100 transition-opacity p-4 hover:bg-neutral-20 rounded-4'
                            >
                              <EditIcon className='text-neutral-60' fontSize='small' />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};
